import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Lock, Loader2 } from "lucide-react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { InstallSection } from "@/components/InstallPrompt";
import PasswordInput from "@/components/PasswordInput";

const inputCls = "w-full h-10 rounded-lg border border-line bg-card px-3 text-[13.5px] text-ink outline-none focus:border-brand focus:ring-2 focus:ring-brand/20";

function Locked({ label, value, hint, mono }) {
  return (
    <div>
      <span className="block text-[12px] font-semibold mb-1.5">{label}</span>
      <div className={`${inputCls} flex items-center gap-2 bg-surface/70`}>
        <span className={mono ? "font-mono text-[13px]" : ""}>{value || <span className="text-muted2">Not set</span>}</span>
        <Lock className="w-3.5 h-3.5 text-muted2 ml-auto shrink-0" aria-label="Set by your publisher" />
      </div>
      {hint && <span className="block text-[11.5px] text-muted2 mt-1">{hint}</span>}
    </div>
  );
}

/** A writer's own details as the publisher keeps them: shown, not edited. */
function WriterDetails() {
  const [me, setMe] = useState(null);
  useEffect(() => {
    api
      .get("/my/profile")
      .then(({ data }) => setMe(data))
      .catch((e) => toast.error(apiError(e)));
  }, []);
  const pub = me?.publisher || "your publisher";
  return (
    <section className="panel panel-pad space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-display text-[18px] font-semibold">You as a writer</h2>
        {me?.signed && <span className="inline-flex px-2.5 py-0.5 rounded-full text-[11.5px] font-semibold text-stock-in bg-emerald-50 dark:bg-emerald-950/40">Signed with {me.publisher}</span>}
      </div>
      {!me ? (
        <Loader2 className="w-4 h-4 animate-spin text-muted2" />
      ) : (
        <>
          <Locked label="Name" value={me.name} hint={`The name on your registrations. If it's wrong, ask ${pub} to change it.`} />
          {me.stage_name && <Locked label="Stage name" value={me.stage_name} />}
          <Locked label="PRO" value={me.pro} />
          <div>
            <span className="block text-[12px] font-semibold mb-1.5">IPI number</span>
            <div className={`${inputCls} flex items-center gap-2`}>
              <span className="font-mono text-[13px]">{me.ipi || <span className="text-muted2 font-sans">Not on file yet — ask {pub}</span>}</span>
              {me.ipi && (
                <button
                  type="button"
                  className="ml-auto inline-flex items-center gap-1 text-[12px] font-semibold text-brand bg-brand-light rounded-full px-2.5 py-0.5"
                  onClick={() => {
                    try {
                      navigator.clipboard.writeText(me.ipi);
                      toast.success("IPI copied");
                    } catch {
                      /* the number is on screen */
                    }
                  }}
                >
                  <Copy className="w-3.5 h-3.5" /> Copy
                </button>
              )}
            </div>
            <span className="block text-[11.5px] text-muted2 mt-1">Your IPI identifies you to BMI, the MLC and other societies. Share it when someone asks to register a song with you.</span>
          </div>
        </>
      )}
    </section>
  );
}

/** Writers: the login email is the publisher's to change; they can add a separate contact email. */
function WriterEmailCard() {
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const [alt, setAlt] = useState(false);
  const [contact, setContact] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api
      .get("/my/profile")
      .then(({ data }) => {
        setMe(data);
        setAlt(!!data.contact_email);
        setContact(data.contact_email || "");
      })
      .catch(() => {});
  }, []);
  const save = async (email) => {
    setBusy(true);
    try {
      const { data } = await api.put("/my/contact-email", { email });
      setMe((m) => ({ ...m, contact_email: data.contact_email }));
      setContact(data.contact_email);
      toast.success(data.contact_email ? `Saved — ${me?.publisher || "your publisher"} will contact you at ${data.contact_email}` : "Contact goes to your login email again");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  const pub = me?.publisher || "your publisher";
  return (
    <section className="panel panel-pad space-y-4">
      <h2 className="font-display text-[18px] font-semibold">Email</h2>
      <Locked label="Login email" value={user?.email} hint={`You sign in with this. Ask ${pub} to change it.`} />
      <label className="flex items-start gap-3 cursor-pointer">
        <button
          type="button"
          role="switch"
          aria-checked={alt}
          onClick={() => {
            const next = !alt;
            setAlt(next);
            if (!next && me?.contact_email) save("");
          }}
          className={`mt-0.5 w-[38px] h-[22px] rounded-full relative shrink-0 transition-colors ${alt ? "bg-brand" : "bg-line"}`}
        >
          <span className={`absolute top-[3px] w-4 h-4 rounded-full bg-white shadow transition-all ${alt ? "left-[19px]" : "left-[3px]"}`} />
        </button>
        <span>
          <span className="block text-[13px] font-semibold">Contact me at a different email</span>
          <span className="block text-[11.5px] text-muted2">{alt ? `${pub} will reach you here instead of your login email.` : `Off: ${pub} contacts you at your login email.`}</span>
        </span>
      </label>
      {alt && (
        <form
          className="space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            save(contact.trim());
          }}
        >
          <span className="block text-[12px] font-semibold">Contact email</span>
          <input className={inputCls} type="email" required placeholder="e.g. manager@example.com" value={contact} onChange={(e) => setContact(e.target.value)} aria-label="Contact email" />
          <span className="block text-[11.5px] text-muted2">Only for contacting you. You still sign in with {user?.email}.</span>
          <Button type="submit" disabled={busy || contact.trim().toLowerCase() === (me?.contact_email || "")} className="bg-brand hover:bg-brand-hover">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save contact email"}
          </Button>
        </form>
      )}
    </section>
  );
}

function EmailCard() {
  const { user, setUser } = useAuth();
  const [f, setF] = useState({ new_email: "", confirm: "", current_password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (f.new_email.trim().toLowerCase() !== f.confirm.trim().toLowerCase()) return toast.error("The two emails don't match");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-email", { new_email: f.new_email.trim(), current_password: f.current_password });
      setUser({ ...user, ...data });
      setF({ new_email: "", confirm: "", current_password: "" });
      toast.success(`Saved — sign in with ${data.email} from now on`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="panel panel-pad space-y-4" onSubmit={submit}>
      <div>
        <h2 className="font-display text-[18px] font-semibold">Email</h2>
        <p className="text-[12.5px] text-muted2 mt-0.5">
          You sign in with <b className="text-ink">{user?.email}</b>.
        </p>
      </div>
      <input className={inputCls} type="email" required placeholder="New email" value={f.new_email} onChange={(e) => setF({ ...f, new_email: e.target.value })} aria-label="New email" autoComplete="email" />
      <input className={inputCls} type="email" required placeholder="New email again" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} aria-label="New email again" autoComplete="off" />
      <PasswordInput className={inputCls} required placeholder="Your password, to confirm" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} aria-label="Your password" autoComplete="current-password" />
      <Button type="submit" disabled={busy} className="bg-brand hover:bg-brand-hover">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Change email"}
      </Button>
    </form>
  );
}

export default function Profile() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [busy, setBusy] = useState(false);

  const save = async (body, msg) => {
    setBusy(true);
    try {
      const { data } = await api.put("/auth/profile", body);
      setUser({ ...user, ...data });
      toast.success(msg);
      setPw({ current_password: "", new_password: "" });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout title="Your profile" subtitle={user?.email}>
      <div className="grid lg:grid-cols-2 gap-4 max-w-4xl">
        {user?.role === "writer" ? (
          <WriterDetails />
        ) : (
        <form
          className="panel panel-pad space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            save({ name }, "Name saved");
          }}
        >
          <h2 className="font-display text-[18px] font-semibold">Your name</h2>
          <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} required aria-label="Name" />
          <Button type="submit" disabled={busy} className="bg-brand hover:bg-brand-hover">
            Save
          </Button>
        </form>
        )}
        {user?.role === "writer" ? <WriterEmailCard /> : <EmailCard />}
        <form
          className="panel panel-pad space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            save(pw, "Password changed");
          }}
        >
          <h2 className="font-display text-[18px] font-semibold">Change password</h2>
          <PasswordInput className={inputCls} placeholder="Current password" required value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} aria-label="Current password" />
          <PasswordInput className={inputCls} placeholder="New password (8+)" minLength={8} required value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} aria-label="New password" />
          <Button type="submit" disabled={busy} className="bg-brand hover:bg-brand-hover">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Change password"}
          </Button>
        </form>
        <div className="lg:col-span-2">
          <InstallSection />
        </div>
      </div>
    </Layout>
  );
}
